// src/store/pvCurtailment.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SdpPoint } from "../types/sdpTypes";

const CACHE_VERSION = 2;

const PV_STORAGE_KEYS = {
  step1: "pvcurtailment.step1",
  wizard: "pvcurtailment.wizard",
  companyId: "pvcurtailment.companyId",
} as const;

type PvCurtailmentState = {
  // fetched lists
  injections: SdpPoint[];
  offtakes: SdpPoint[];
  // selections
  injectionEAN: string;
  offtakeEAN: string; // allow "none"
  siteName: string;
  selectedInjection: SdpPoint | null;
  selectedOfftake: SdpPoint | null;

  // new persisted fields (step2)
  hasPartner: "yes" | "no" | "";
  partnerId: string;
  controller: string;
  controllerId: number | null;
  inverterPower: number | null;
  opportunityCost: number | null;

  // contract fields (step3)
  desiredStartDate: string;
  actualStartDate: string;
  contractEndType: "auto" | "date";
  specifiedEndDate: string;

  // tracks whether the user has manually edited the site name
  siteNameManuallyEdited: boolean;

  // step1 confirmation
  flexConfirmed: boolean;

  // ui state
  loadingInjections: boolean;
  loadingOfftakes: boolean;
  errorInjections: string | null;
  errorOfftakes: string | null;
};

function hydratePersistedState(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(PV_STORAGE_KEYS.step1);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed._cacheVersion !== CACHE_VERSION) {
      localStorage.removeItem(PV_STORAGE_KEYS.step1);
      localStorage.removeItem(PV_STORAGE_KEYS.wizard);
      return {};
    }
    return parsed;
  } catch {
    localStorage.removeItem(PV_STORAGE_KEYS.step1);
    return {};
  }
}

const persisted = hydratePersistedState();

const initialState: PvCurtailmentState = {
  injections: [],
  offtakes: [],
  injectionEAN: (persisted.injectionEAN as string) ?? "",
  offtakeEAN: (persisted.offtakeEAN as string) ?? "",
  siteName: (persisted.siteName as string) ?? "",
  selectedInjection: (persisted.selectedInjection as SdpPoint) ?? null,
  selectedOfftake: (persisted.selectedOfftake as SdpPoint) ?? null,

  // hydrate new fields
  hasPartner: (persisted.hasPartner as "yes" | "no" | "") ?? "",
  partnerId: (persisted.partnerId as string) ?? "",
  controller: (persisted.controller as string) ?? "",
  controllerId: (persisted.controllerId as number) ?? null,
  inverterPower: (persisted.inverterPower as number) ?? null,
  opportunityCost: (persisted.opportunityCost as number) ?? null,

  // contract fields
  desiredStartDate: (persisted.desiredStartDate as string) ?? "",
  actualStartDate: (persisted.actualStartDate as string) ?? "",
  contractEndType: (persisted.contractEndType as "auto" | "date") ?? "auto",
  specifiedEndDate: (persisted.specifiedEndDate as string) ?? "",

  siteNameManuallyEdited: (persisted.siteNameManuallyEdited as boolean) ?? false,

  // step1 confirmation
  flexConfirmed: (persisted.flexConfirmed as boolean) ?? false,

  loadingInjections: false,
  loadingOfftakes: false,
  errorInjections: null,
  errorOfftakes: null,
};

const pvCurtailmentSlice = createSlice({
  name: "pvCurtailment",
  initialState,
  reducers: {
    // lists
    setInjections(state, action: PayloadAction<SdpPoint[]>) {
      state.injections = action.payload;
    },
    setOfftakes(state, action: PayloadAction<SdpPoint[]>) {
      state.offtakes = action.payload;
    },
    setLoadingInjections(state, action: PayloadAction<boolean>) {
      state.loadingInjections = action.payload;
      if (action.payload) state.errorInjections = null;
    },
    setLoadingOfftakes(state, action: PayloadAction<boolean>) {
      state.loadingOfftakes = action.payload;
      if (action.payload) state.errorOfftakes = null;
    },
    setErrorInjections(state, action: PayloadAction<string | null>) {
      state.errorInjections = action.payload;
    },
    setErrorOfftakes(state, action: PayloadAction<string | null>) {
      state.errorOfftakes = action.payload;
    },

    // selections with full objects
    setInjectionSelection(state, action: PayloadAction<{ ean: string; injection: SdpPoint | null }>) {
      state.injectionEAN = action.payload.ean;
      state.selectedInjection = action.payload.injection;
      // reset offtake selection when injection changes
      state.offtakeEAN = "";
      state.selectedOfftake = null;
      persist(state);
    },
    setOfftakeSelection(state, action: PayloadAction<{ ean: string; offtake: SdpPoint | null }>) {
      state.offtakeEAN = action.payload.ean;
      state.selectedOfftake = action.payload.offtake;
      persist(state);
    },
    setSiteName(state, action: PayloadAction<string>) {
      state.siteName = action.payload;
      persist(state);
    },
    setSiteNameManuallyEdited(state, action: PayloadAction<boolean>) {
      state.siteNameManuallyEdited = action.payload;
      persist(state);
    },

    // new reducers for step2 fields
    setHasPartner(state, action: PayloadAction<"yes" | "no" | "">) {
      state.hasPartner = action.payload;
      // if user switches away from "yes" clear partnerId
      if (action.payload !== "yes") state.partnerId = "";
      persist(state);
    },
    setPartnerId(state, action: PayloadAction<string>) {
      state.partnerId = action.payload;
      persist(state);
    },
    setController(state, action: PayloadAction<{ name: string; id: number | null }>) {
      state.controller = action.payload.name;
      state.controllerId = action.payload.id;
      persist(state);
    },
    setInverterPower(state, action: PayloadAction<number | null>) {
      state.inverterPower = action.payload;
      persist(state);
    },
    setOpportunityCost(state, action: PayloadAction<number | null>) {
      state.opportunityCost = action.payload;
      persist(state);
    },

    // contract reducers (step3)
    setDesiredStartDate(state, action: PayloadAction<string>) {
      state.desiredStartDate = action.payload;
      persist(state);
    },
    setActualStartDate(state, action: PayloadAction<string>) {
      state.actualStartDate = action.payload;
      persist(state);
    },
    setContractEndType(state, action: PayloadAction<"auto" | "date">) {
      state.contractEndType = action.payload;
      // clear specified end date when switching to auto
      if (action.payload === "auto") state.specifiedEndDate = "";
      persist(state);
    },
    setSpecifiedEndDate(state, action: PayloadAction<string>) {
      state.specifiedEndDate = action.payload;
      persist(state);
    },
    setFlexConfirmed(state, action: PayloadAction<boolean>) {
      state.flexConfirmed = action.payload;
      persist(state);
    },

    resetStep1(state) {
      state.injectionEAN = "";
      state.selectedInjection = null;
      state.offtakeEAN = "";
      state.selectedOfftake = null;
      state.siteName = "";
      state.siteNameManuallyEdited = false;
      state.flexConfirmed = false;
      state.hasPartner = "";
      state.partnerId = "";
      state.controller = "";
      state.controllerId = null;
      state.inverterPower = null;
      state.opportunityCost = null;
      state.desiredStartDate = "";
      state.actualStartDate = "";
      state.contractEndType = "auto";
      state.specifiedEndDate = "";
      persist(state);
    },
    clearDownstreamSteps(state) {
      state.hasPartner = "";
      state.partnerId = "";
      state.controller = "";
      state.controllerId = null;
      state.inverterPower = null;
      state.opportunityCost = null;
      state.desiredStartDate = "";
      state.actualStartDate = "";
      state.contractEndType = "auto";
      state.specifiedEndDate = "";
      state.siteNameManuallyEdited = false;
      state.flexConfirmed = false;
      persist(state);
    },
    resetApiData(state) {
      state.injections = [];
      state.offtakes = [];
      state.loadingInjections = false;
      state.loadingOfftakes = false;
      state.errorInjections = null;
      state.errorOfftakes = null;
    },
    prefillFromContractData(
      state,
      action: PayloadAction<{
        partner_id: number | null;
        controller_id: number | null;
        controller_name: string | null;
        nominal_power: number | null;
        opportunity_cost: number | null;
        start_date: string | null;
        end_date: string | null;
      }>
    ) {
      const d = action.payload;
      state.hasPartner = d.partner_id != null ? "yes" : "";
      state.partnerId = d.partner_id != null ? String(d.partner_id) : "";
      state.controllerId = d.controller_id;
      state.controller = d.controller_name ?? "";
      state.inverterPower = d.nominal_power;
      state.opportunityCost = d.opportunity_cost;
      state.desiredStartDate = d.start_date ?? "";
      state.contractEndType = d.end_date ? "date" : "auto";
      state.specifiedEndDate = d.end_date ?? "";
      persist(state);
    },
  },
});

function persist(state: PvCurtailmentState) {
  const payload = {
    _cacheVersion: CACHE_VERSION,
    injectionEAN: state.injectionEAN,
    offtakeEAN: state.offtakeEAN,
    siteName: state.siteName,
    selectedInjection: state.selectedInjection,
    selectedOfftake: state.selectedOfftake,
    siteNameManuallyEdited: state.siteNameManuallyEdited,
    hasPartner: state.hasPartner,
    partnerId: state.partnerId,
    controller: state.controller,
    controllerId: state.controllerId,
    inverterPower: state.inverterPower,
    opportunityCost: state.opportunityCost,
    desiredStartDate: state.desiredStartDate,
    actualStartDate: state.actualStartDate,
    contractEndType: state.contractEndType,
    specifiedEndDate: state.specifiedEndDate,
    flexConfirmed: state.flexConfirmed,
  };
  localStorage.setItem(PV_STORAGE_KEYS.step1, JSON.stringify(payload));
}

/**
 * Clears all PV curtailment localStorage entries.
 * Call this from any component — the Redux store should be reset separately
 * via `dispatch(resetStep1())` and optionally `dispatch(resetApiData())`.
 */
export function clearAllPvCurtailmentCache() {
  localStorage.removeItem(PV_STORAGE_KEYS.step1);
  localStorage.removeItem(PV_STORAGE_KEYS.wizard);
  localStorage.removeItem(PV_STORAGE_KEYS.companyId);
}

export { PV_STORAGE_KEYS };

export const {
  setInjections,
  setOfftakes,
  setLoadingInjections,
  setLoadingOfftakes,
  setErrorInjections,
  setErrorOfftakes,
  setInjectionSelection,
  setOfftakeSelection,
  setSiteName,
  setSiteNameManuallyEdited,
  setHasPartner,
  setPartnerId,
  setController,
  setInverterPower,
  setOpportunityCost,
  setDesiredStartDate,
  setActualStartDate,
  setContractEndType,
  setSpecifiedEndDate,
  setFlexConfirmed,
  resetStep1,
  clearDownstreamSteps,
  resetApiData,
  prefillFromContractData,
} = pvCurtailmentSlice.actions;

export default pvCurtailmentSlice.reducer;
